// Faq.js — Seção "Perguntas Feitas Frequentemente".
//
// LAYOUT: cabeçalho e busca centralizados, cartões em DUAS colunas ocupando a
// largura toda. A ilustração que ficava à direita saiu a pedido do dono — ela
// não conversava mais com o acabamento novo, e o espaço que ela ocupava é
// justamente o que faz a lista caber em metade da altura: sete perguntas
// empilhadas numa coluna só rendiam uma torre estreita e comprida.
//
// DUAS PILHAS INDEPENDENTES, e não uma grade de linhas. Numa grade, abrir uma
// pergunta alta na coluna da esquerda empurra a LINHA inteira: o cartão da
// direita fica curto e sobra um buraco do tamanho da resposta ao lado dele.
// Com duas colunas que são cada uma a sua própria pilha, crescer um cartão só
// desloca o que está ABAIXO dele, na mesma coluna. A divisão é por metades
// (e não alternada) porque no celular as pilhas viram uma só, e por metades a
// ordem de leitura continua 1, 2, 3… — alternando sairia 1, 3, 5, 2, 4.
//
// ACABAMENTO: cada pergunta é um CARTÃO independente,
// com ícone de "+" que gira virando "×" ao abrir e um brilho que segue o
// cursor. Veio de um componente de referência genérico, do qual entrou SÓ o
// desenho — o tema claro/escuro dele (paletas em JS, MutationObserver,
// localStorage) foi descartado inteiro, porque este site já resolve tema por
// `data-theme` no <html> + as variáveis --c-* do App.css. Toda cor aqui sai
// dos tokens do Tailwind e acompanha o tema sozinha.
//
// Também ficaram de fora da referência, por não terem função nesta seção:
// o `min-h-screen` com aurora de fundo (isto é uma seção da home, não uma
// página) e a pílula "Signal FAQ" com beam girando (enfeite de demonstração).
import React, { useMemo, useState } from "react";
import RevealStagger from "../ui/RevealStagger";
import FitaAdesiva from "../ui/FitaAdesiva";
import "./Faq.css";
import { TituloDeSecao } from "../ui/TituloDeSecao";

// Ângulos das fitas — DUAS listas, uma por canto, porque cada cartão leva fita
// nos dois lados. Nada aqui é simétrico de propósito: fita colada à mão não sai
// duas vezes no mesmo ângulo, e um par espelhado nos dois cantos leria como
// carimbo. As listas dão a volta com `%`, então acrescentar pergunta não exige
// mexer aqui.
const ANGULOS_ESQ = [-9, 7, -16, 12, -4, 18, -12];
const ANGULOS_DIR = [14, -11, 5, -19, 16, -7, 10];

// `id` é a chave de tudo: identifica a pergunta aberta (não o índice, que
// mudava de dono quando a busca filtrava a lista) e monta os ids de
// aria-controls/aria-labelledby, que precisam ser estáveis entre renders.
// `categoria` é rótulo de organização, não conteúdo novo: só nomeia o assunto
// que a própria pergunta já trata.
const QUESTIONS = [
  {
    id: "contato",
    categoria: "Contato",
    q: "Como faço para entrar em contato?",
    a: "Nosso atendimento é rápido e simplificado via WhatsApp. Basta nos chamar pelo número disponível no site e também no link da bio do nosso instagram para tirar dúvidas, enviar seu original ou acompanhar seu processo de publicação. Nossa equipe estará plenamente à sua disposição.",
  },
  {
    id: "catalogo",
    categoria: "Catálogo",
    q: "Como posso encontrar os livros publicados?",
    a: "Todos os livros ficam disponíveis para compra diretamente no nosso site, de forma simples e atrativa para os leitores.",
  },
  {
    id: "originais",
    categoria: "Originais",
    q: "Como envio meu original para avaliação?",
    a: "Você pode enviar seu original diretamente pelo WhatsApp. Inicialmente precisaremos do seu arquivo Word para avaliação; as demais informações necessárias serão alinhadas durante o atendimento. Em aproximadamente 24 h você receberá um retorno.",
  },
  {
    id: "prazo",
    categoria: "Prazos",
    q: "Quanto tempo leva para publicar meu livro?",
    a: "Nosso processo é rápido: publicamos sua obra em até 7 dias após a aprovação do original e o envio dos materiais necessários.",
  },
  {
    id: "impressao",
    categoria: "Impressão",
    q: "A publicação já inclui as cópias físicas?",
    a: "O investimento inicial para o pacote de publicação não engloba as cópias físicas, mas podemos cuidar disso para você sob demanda.",
  },
  {
    id: "royalties",
    categoria: "Royalties",
    q: "O autor recebe alguma porcentagem pelas vendas?",
    a: "Sim, o autor recebe um percentual sobre o valor de cada venda. As condições são alinhadas com o editor-chefe.",
  },
  {
    id: "pacote",
    categoria: "Publicação",
    q: "O que está incluso no pacote completo de publicação?",
    a: "Editoração completa do livro físico e digital, diagramação, projeto gráfico, adequação às normas da ABNT, design da capa, ficha catalográfica e ISBN.",
  },
];

const CartaoFaq = ({ id, pergunta, resposta, categoria, aberto, onToggle, anguloEsq, anguloDir }) => {
  const idBotao = `faq-pergunta-${id}`;
  const idPainel = `faq-resposta-${id}`;

  // Duas coordenadas por movimento do ponteiro, e nada mais: o gradiente do
  // brilho mora no ::before do cartão (Faq.css) e lê --faq-x/--faq-y. Escrever
  // variável CSS em vez de re-renderizar mantém o React fora do caminho do
  // mousemove. Quem pediu menos movimento nem chega aqui — a regra de
  // prefers-reduced-motion apaga o pseudo-elemento.
  const seguirCursor = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--faq-x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--faq-y", `${e.clientY - r.top}px`);
  };
  const soltarCursor = (e) => {
    e.currentTarget.style.removeProperty("--faq-x");
    e.currentTarget.style.removeProperty("--faq-y");
  };

  return (
    <div
      onMouseMove={seguirCursor}
      onMouseLeave={soltarCursor}
      data-aberto={aberto ? "true" : "false"}
      className={
        // `border-[1px]` e NÃO `border`: o bootstrap.min.css declara
        // `.border { border: … !important }`, o que sequestraria a cor laranja
        // do cartão aberto e a devolveria como hairline cinza (ver o bloco
        // "CONFLITO COM BOOTSTRAP" no App.css).
        "faq-cartao relative rounded-xl border-[1px] backdrop-blur-sm " +
        "transition-[transform,background-color,border-color] duration-300 " +
        "hover:-translate-y-0.5 focus-within:-translate-y-0.5 " +
        (aberto
          ? "border-insigne-orange/40 bg-insigne-navy-mid/70"
          : "border-line bg-insigne-navy-soft/70")
      }
    >
      {/* Uma fita em CADA quina de cima, metade para dentro e metade para
          fora — é o que faz ler como algo colado, e não como adesivo pousado
          no meio do cartão. Os deslocamentos negativos são ~metade da largura.
          A da direita fica 4 px mais alta que a da esquerda: alturas iguais
          nos dois cantos devolvem a simetria que os ângulos diferentes estão
          tentando quebrar.
          As fitas da direita de uma coluna ficam de frente para as da esquerda
          da coluna vizinha — é por isso que a calha entre as colunas é larga
          (`md:gap-x-12` lá embaixo) em vez do respiro padrão: com a calha
          estreita as duas se tocavam no meio.
          z-0 contra o z-10 do conteúdo, para a ponta que cai para dentro
          passar POR BAIXO da pergunta. */}
      <FitaAdesiva className="z-0 -left-4 -top-4 text-insigne-orange/60" rotacao={anguloEsq} largura={40} />
      <FitaAdesiva className="z-0 -right-4 -top-5 text-insigne-orange/60" rotacao={anguloDir} largura={38} />

      <h3 className="relative z-10">
        <button
          type="button"
          id={idBotao}
          onClick={onToggle}
          aria-expanded={aberto}
          aria-controls={idPainel}
          className="faq-gatilho foco-laranja w-full flex items-center gap-4 py-4 pl-7 pr-5 text-left"
        >
          <span className="relative shrink-0">
            {/* Anel pulsando só no item aberto: marca qual pergunta está em
                leitura quando o olho volta para a lista. */}
            {aberto && (
              <span
                aria-hidden="true"
                className="faq-anel-pulso absolute inset-0 rounded-full border-[1px]
                           border-insigne-orange/50 animate-ping"
              />
            )}
            {/* O "+" gira 45° e vira "×" — o mesmo traço serve de "abrir" e de
                "fechar", sem trocar de ícone no meio da transição. Laranja da
                marca, que é o acento do site. */}
            <span
              aria-hidden="true"
              className={
                "relative flex h-9 w-9 items-center justify-center rounded-full " +
                "border-[1px] border-insigne-orange/40 bg-insigne-orange/10 " +
                "text-insigne-orange transition-transform duration-500 ease-out " +
                (aberto ? "rotate-45" : "")
              }
            >
              <i className="bi bi-plus-lg text-sm" />
            </span>
          </span>

          {/* No celular a etiqueta desce para baixo da pergunta (coluna); a
              partir de sm ela vai para a ponta direita da linha. Espremida ao
              lado num telefone, sobrariam duas palavras por linha na pergunta. */}
          <span
            className="flex min-w-0 flex-1 flex-col items-start gap-2
                       sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <span className="font-medium leading-snug text-white">{pergunta}</span>
            {categoria && (
              // `border-white/15` e não `border-line`: no tema escuro a hairline
              // (#191B44) é praticamente da cor do cartão e a pílula sumia. O
              // branco temável a 15 % dá contorno nos dois temas — e escapa do
              // override `html .border-white` do App.css, que só pega a classe
              // exata, sem opacidade.
              <span
                className="shrink-0 rounded-full border-[1px] border-white/15 px-2.5 py-0.5
                           text-[10px] uppercase tracking-[0.18em] text-white/55"
              >
                {categoria}
              </span>
            )}
          </span>
        </button>
      </h3>

      {/* Abertura por grid-template-rows: é a única forma em CSS puro de animar
          "altura automática" sem medir o conteúdo em JavaScript. */}
      <div
        id={idPainel}
        role="region"
        aria-labelledby={idBotao}
        className="relative z-10 grid transition-[grid-template-rows] duration-500 ease-out"
        style={{ gridTemplateRows: aberto ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden" aria-hidden={!aberto}>
          {/* Recuo até a coluna da pergunta (px-5 + ícone 36px + gap 16px):
              a resposta lê como continuação dela, não como um bloco solto. */}
          <p className="pb-5 pl-7 pr-5 sm:pl-[5rem] sm:pr-8 text-sm leading-relaxed text-white/70">
            {resposta}
          </p>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [busca, setBusca] = useState("");
  const [aberta, setAberta] = useState(QUESTIONS[0].id);

  // Filtro de perguntas pelo termo digitado
  const filtradas = useMemo(() => {
    if (!busca.trim()) return QUESTIONS;
    const t = busca.toLowerCase();
    return QUESTIONS.filter(
      (i) => i.q.toLowerCase().includes(t) || i.a.toLowerCase().includes(t)
    );
  }, [busca]);

  // Metade para cada pilha. `ordem` é a posição na lista JÁ FILTRADA e viaja
  // junto com o item: é dela que saem o ângulo da fita e o canto em que ela
  // senta, e sem isso os dois voltariam a se repetir a cada coluna.
  // Ímpar sobrando vai para a esquerda (`ceil`), que é a coluna que o olho
  // percorre primeiro.
  const colunas = useMemo(() => {
    const comOrdem = filtradas.map((item, ordem) => ({ ...item, ordem }));
    const corte = Math.ceil(comOrdem.length / 2);
    return [comOrdem.slice(0, corte), comOrdem.slice(corte)];
  }, [filtradas]);

  return (
    <section id="faq" className="px-4 md:px-8 py-10">
      <div className="max-w-[1280px] mx-auto">
        {/* Cabeçalho centralizado: com os cartões ocupando a largura toda, um
            título encostado à esquerda ficaria pendurado num canto de 1280px. */}
        <TituloDeSecao
          centro
          chapeu="Dúvidas"
          titulo="Perguntas frequentes"
          subtitulo="Dê uma olhada, talvez sua pergunta já tenha sido respondida."
          className="mb-[2rem]"
        />

        {/* Busca centralizada. Sem brilho em repouso: a versão anterior tinha
            um halo laranja permanente atrás do campo e virou uma mancha no
            navy. Ficou só o anel de foco, que agora é a classe global
            `.campo-foco` do App.css e vale também no cupom do checkout. */}
        <div className="campo-foco faq-busca relative mx-auto mb-8 max-w-[520px]">
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Busque sua dúvida..."
            className="relative w-full px-5 py-3 rounded-full bg-white/5 border border-line
                       text-white placeholder:text-white/40 outline-none
                       focus:border-insigne-orange focus:bg-white/10 transition-colors"
          />
          <i className="bi bi-search absolute right-5 top-1/2 -translate-y-1/2 text-white/60" />
        </div>

        {/* Duas pilhas lado a lado (ver o cabeçalho do arquivo). No celular
            `grid-cols-1` as empilha, e a divisão por metades garante que a
            ordem de leitura continue a original. */}
        {/* `gap-x-12` (48 px) e não o respiro padrão: cada fita vaza ~17 px
            para fora do cartão, então duas colunas separadas por 24 px teriam
            as fitas de frente uma para a outra se encostando no meio da calha.
            Também não se usa `gap-4` aqui — o bootstrap.min.css redefine toda
            a escala 0–5 com !important, e `gap-x-12`/`gap-y-4` estão fora do
            alcance dele. */}
        <div className="grid items-start gap-y-3 md:grid-cols-2 md:gap-x-12 md:gap-y-4">
          {colunas.map((coluna, iColuna) => (
            <RevealStagger key={iColuna} className="space-y-4 md:space-y-5">
              {coluna.map((item) => (
                <RevealStagger.Item key={item.id} className="faq-entrada">
                  <CartaoFaq
                    id={item.id}
                    pergunta={item.q}
                    resposta={item.a}
                    categoria={item.categoria}
                    anguloEsq={ANGULOS_ESQ[item.ordem % ANGULOS_ESQ.length]}
                    anguloDir={ANGULOS_DIR[item.ordem % ANGULOS_DIR.length]}
                    aberto={aberta === item.id}
                    onToggle={() =>
                      setAberta((atual) => (atual === item.id ? null : item.id))
                    }
                  />
                </RevealStagger.Item>
              ))}
            </RevealStagger>
          ))}
        </div>
        {/* Fora do RevealStagger de propósito: o contêiner clona cada filho
            injetando `estado`/`indice`, e num <p> isso viraria atributo
            desconhecido no DOM. */}
        {filtradas.length === 0 && (
          <p className="p-6 text-white/70 text-center">
            Nenhuma pergunta encontrada.
          </p>
        )}

        {/* Rodapé da seção — centralizado junto com o resto, agora que não há
            mais uma ilustração à direita puxando o eixo. */}
        <div className="mx-auto mt-8 max-w-[520px] border-t border-line pt-4 text-center text-sm">
          <p className="text-white/80">Não encontrou suas respostas aqui?</p>
          <p>
            Entre em contato com o{" "}
            <a href="https://wa.link/o4jzw4" className="!text-insigne-orange underline">
              Suporte
            </a>{" "}
            ou{" "}
            <a href="https://wa.link/o4jzw4" className="!text-insigne-orange underline">
              Envie sua pergunta
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
